import clsx from "clsx";
import { FC, useEffect, useState } from "react";
import {
  Asset,
  isThor,
  Network,
  networkLabel,
  validateAddress,
} from "rujira.js";
import {
  AssetLabel,
  BreakPoints,
  i18n,
  IconDenom,
  Input,
  NetworkIcon,
  Provider,
  useWindowSize,
} from "rujira.ui";
import { AngleLeft, MinusCircle } from "rujira.ui/src/components/icons/Icons";
import { ProviderIcon } from "../../common/components/Header";

export type Destination = {
  address: string;
  asset: Asset & { price?: { current?: bigint } };
};

export interface DestinationOption {
  asset: Asset;
  address: string;
  network: Network;
  providers: Provider.Key[];
  Component?: FC<Omit<DestinationOption, "Component">>;
}

export const DestinationSelectModal: FC<{
  to?: string;
  setTo: (v: string) => void;
  destination?: Destination;
  setDestination: (v: Destination) => void;
  options: DestinationOption[];
}> = ({ options: options_, setDestination, destination, to, setTo }) => {
  // Store a temporary value - we don't want to commit until the final selection
  const [to_, setTo_] = useState(to);
  const [step, setStep] = useState(0);
  const { width } = useWindowSize();
  const options = options_.filter((a) => a.asset.metadata.symbol === to_);

  return (
    <div className="row flex-overflow mt-2">
      {((width < BreakPoints.medium && step === 0) ||
        width >= BreakPoints.medium) && (
        <div className="col-12 col-md-5 flex dir-c">
          <DestinationAssetSelect
            to={to_}
            options={options_}
            setTo={(v) => {
              setStep(1);
              setTo_(v);
            }}
          />
        </div>
      )}
      {((width < BreakPoints.medium && step === 1) ||
        width >= BreakPoints.medium) && (
        <div className="col-12 col-md-7 flex dir-c">
          {width < BreakPoints.medium && (
            <div className="px-1.5 mb-0.5">
              <button
                className="transparent fs-12 color-grey hover-white flex ai-c"
                onClick={() => setStep(0)}>
                <AngleLeft className="w-2 h-2 mr-0.5" />
                Back
              </button>
            </div>
          )}
          {to_ && (
            <>
              <div
                className="pr-2 ml-2.5 flex ai-c condensed fs-18 fw-500 no-shrink"
                style={{ height: "2.875rem" }}>
                <span className="color-grey">Select preferred</span>
                <IconDenom denom={to_} className="h-3.5 w-3.5 block mx-1" />
                <span>{to_} wallet</span>
              </div>
              <div className="overflow-y common-scrollbars flex dir-c gap-1 pl-2 pr-2 pt-1 pb-2">
                <DestinationSelect
                  destination={destination}
                  setDestination={(d) => {
                    setDestination(d);
                    setTo(to_);
                  }}
                  options={options}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const DestinationSelect: FC<{
  destination?: Destination;
  setDestination: (v: Destination) => void;
  options: DestinationOption[];
}> = ({ options, destination, setDestination }) => {
  useEffect(() => {
    const def = options[0];
    if (def && !destination) setDestination(def);
  }, [options]);

  return (
    <>
      {options

        .sort((a, b) => {
          const aSym = a.asset.metadata.symbol;
          const bSym = b.asset.metadata.symbol;

          // First group by symbol
          const symCmp = aSym.localeCompare(bSym);
          if (symCmp !== 0) return symCmp;

          const aChain = a.asset.chain;
          const bChain = b.asset.chain;

          // Priority scoring
          const score = (asset: typeof a) => {
            const isSec = asset.asset.type === "SECURED";
            const isEth = asset.asset.chain === "ETH";

            // Highest: SECURED + ETH
            if (isSec && isEth) return 0;

            // Next: SECURED on own-chain (symbol = chain)
            if (
              isSec &&
              !["USDC", "USDT"].includes(asset.asset.metadata.symbol)
            )
              return 1;

            // Next: L1 ETH
            if (!isSec && isEth) return 2;

            // Next: L1 non-ETH chains (alphabetical later)
            if (!isSec && !isEth) return 3;

            // Last: Remaining SECURED
            return 4;
          };

          const aScore = score(a);
          const bScore = score(b);

          if (aScore !== bScore) return aScore - bScore;

          //
          // If same scoring bucket, order by chain alphabetically
          //
          const chainCmp = aChain.localeCompare(bChain);
          if (chainCmp !== 0) return chainCmp;

          //
          // Final tiebreak: address
          //
          return a.address.localeCompare(b.address);
        })
        .map((x) => (
          <Item
            key={x.address + x.asset.asset + x.network}
            {...x}
            setDestination={setDestination}
            destination={destination}
          />
        ))}
    </>
  );
};

const Item: FC<
  {
    destination?: Destination;
    setDestination: (d: Destination) => void;
  } & DestinationOption
> = ({
  address,
  network,
  providers,
  asset,
  destination,
  setDestination,
  Component,
}) => {
  const isSelected =
    destination?.address === address && asset.asset === destination.asset.asset;
  return (
    <a
      onClick={() => {
        setDestination({
          address,
          asset,
        });
      }}
      className={clsx({
        "condensed p-1.5 flex ai-c card b-hover-grey": true,
        "b-black": !isSelected,
        "b-white": isSelected,
        "glow-primary1": network === "THOR",
      })}>
      <div className="lp">
        <IconDenom denom={asset.metadata.symbol} className="w-3 h-3 block" />
        <NetworkIcon network={network} className="w-3 h-3 block icon-denom" />
      </div>
      <p className="ml-1 fw-500 color-white flex dir-c">
        <span>
          <AssetLabel
            asset={asset}
            Container={({ children }) => (
              <span className="color-grey fs-14 fs-lg-16">{children}</span>
            )}
          />{" "}
          <span className="color-grey">on</span> {networkLabel(network)}
        </span>
        <span className="block color-grey fs-14">
          {address.substring(0, 12)}…{address.slice(-12)}
        </span>
      </p>
      {providers.map((p) => (
        <ProviderIcon key={p} provider={p} className="w-2 h-2 ml-a" selected />
      ))}
      {Component && (
        <Component
          asset={asset}
          address={address}
          network={network}
          providers={providers}
        />
      )}
    </a>
  );
};

const DestinationAssetSelect: FC<{
  to?: string;
  setTo: (v: string) => void;
  options: DestinationOption[];
}> = ({ options, to, setTo }) => {
  const [search, setSearch] = useState("");
  const filtered = [
    ...new Set(options.map((a) => a.asset.metadata.symbol)),
  ].filter((a) =>
    search ? a.toLowerCase().includes(search.toLowerCase()) : true
  );

  return (
    <>
      <div className="pl-2">
        <Input
          value={search || ""}
          onChange={(e) => setSearch(e.currentTarget.value)}
          label="Search tokens">
          {search && (
            <MinusCircle
              className="input-container__clear"
              onClick={() => setSearch("")}
            />
          )}
        </Input>
      </div>
      <div className="overflow-y common-scrollbars flex dir-c gap-1 pl-2 mt-1 pr-1 mb-2">
        {filtered.length === 0 && (
          <i18n.p className="color-grey condensed fs-14 as-c p-2">
            No search results found
          </i18n.p>
        )}
        {filtered.map((a) => (
          <a
            key={a}
            onClick={() => {
              setTo(a);
            }}
            className={clsx({
              "condensed p-1.5 flex ai-c card b-hover-grey": true,
              "b-black": to !== a,
              "b-white": to === a,
            })}>
            <IconDenom denom={a} className="h-3 w-3 block" />
            <p className="ml-1 fw-500 color-white">{a}</p>
          </a>
        ))}
      </div>
    </>
  );
};

export const collectDestinationOptions =
  (
    accounts: { address: string; provider: Provider.Key }[],
    Component?: FC<Omit<DestinationOption, "Component">>
  ) =>
  (asset: Asset): DestinationOption[] => {
    const secured: DestinationOption[] = asset.variants?.secured
      ? accounts
          .filter((a) => isThor(a.address))
          .map((a) => ({
            asset: asset.variants!.secured!,
            address: a.address,
            network: "THOR",
            providers: [a.provider],
            Component,
          }))
      : [];

    return accounts.reduce(
      (a: DestinationOption[], v) =>
        validateAddress(
          asset.type === "SECURED" ? "THOR" : asset.chain,
          v.address
        )
          ? [
              {
                asset,
                address: v.address,
                network: asset.chain,
                providers: [v.provider],
                Component,
              },
              ...a,
            ]
          : a,
      secured
    );
  };
